import * as React from 'react'
import { ConfirmModal } from '@/modules/drivers/modals/ConfirmModal'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

/**
 * Wiersz z serwera: "Nazwisko Imie|liczbaDni|dd.MM-dd.MM|DE,FR", rekordy rozdzielone
 * nowa linia. Nazwiska i listy krajow zawieraja przecinki, wiec przecinek nie moze
 * rozdzielac rekordow. Wiersz bez "|" traktujemy jako samo nazwisko - komunikat ze
 * starszego serwera nadal sie wyswietli.
 */
type ForeignTripEntry = {
  name: string
  days: number
  range: string
  countries: string
}

const parseForeignTripEntries = (message: string): ForeignTripEntry[] =>
  message
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line !== '')
    .map((line) => {
      const [name, days, range, countries] = line.split('|')
      const parsedDays = Number(days)
      return {
        name: (name ?? '').trim(),
        days: Number.isFinite(parsedDays) ? parsedDays : 0,
        range: (range ?? '').trim(),
        countries: (countries ?? '').trim(),
      }
    })
    .filter((entry) => entry.name !== '')

/**
 * Potwierdzenie dla kierowcow, ktorzy maja aktywnosci zagraniczne w liczonym miesiacu,
 * ale nie maja na niego delegacji.
 *
 * Wykrywa to serwer w kroku precalculate (OperationResult z kodem
 * TIMESHEET_FOREIGN_ACTIVITY_WITHOUT_TRIP), bo tylko on widzi wpisy krajow i delegacje.
 * Dlatego pytamy MIEDZY precalculate a wlasciwym przeliczeniem - inaczej niz przy braku
 * umowy, ktory grid zna z wlasnych danych i moze zapytac zanim cokolwiek ruszy.
 *
 * Zwraca `requestConfirm`, ktore czeka na decyzje uzytkownika: `true` = licz mimo to,
 * `false` = przerwij. Zamkniecie krzyzykiem liczy sie jako przerwanie - przeliczenie z
 * zerowa czescia zagraniczna zawyza nagrode i brutto, wiec cisza nie moze oznaczac zgody.
 */
export const useForeignTripConfirm = () => {
  const { t } = useLocalize()
  const [driverNames, setDriverNames] = React.useState<string | null>(null)
  const resolveRef = React.useRef<((confirmed: boolean) => void) | null>(null)

  const settle = React.useCallback((confirmed: boolean) => {
    setDriverNames(null)
    const resolve = resolveRef.current
    resolveRef.current = null
    resolve?.(confirmed)
  }, [])

  const requestConfirm = React.useCallback((names: string) => {
    // Nadchodzace pytanie nie moze zostawic poprzedniego wiszacego w nieskonczonosc.
    resolveRef.current?.(false)
    setDriverNames(names)
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve
    })
  }, [])

  const content = React.useMemo<React.ReactNode[]>(() => {
    if (!driverNames) return []
    return parseForeignTripEntries(driverNames).map((entry, index) => (
      <React.Fragment key={`${entry.name}-${index}`}>
        {entry.name}
        {entry.days > 0 && (
          <span className="tw-text-content-muted">
            {' '}
            ({entry.days}{' '}
            {entry.days === 1
              ? t('timesheet.day-singular')
              : t('timesheet.day-plural')}
            : {entry.range}
            {entry.countries ? `, ${entry.countries}` : ''})
          </span>
        )}
      </React.Fragment>
    ))
  }, [driverNames, t])

  const modal = (
    <ConfirmModal
      isOpen={driverNames !== null}
      onClose={() => settle(false)}
      title={t('timesheet.insufficient-data-title')}
      text={t('timesheet.foreign-activity-without-trip-title')}
      content={content}
      actionText={t('timesheet.calculate-anyway')}
      onConfirm={() => settle(true)}
    />
  )

  return { requestConfirm, modal }
}
